import { Copy, Loader2, LogIn, Plus } from "lucide-react";
import { ConnectionLogo } from "./ConnectionLogo";
import { StatusPill } from "./StatusPill";
import { cardMeta, sharingAction, sharingLists } from "./connectionsModel";

// The tools shared between this brand and the brand it is hosted with (a brand profile and its host). One
// section per partner: what the partner has that this brand can take, then what this brand has that it can give. A
// key copies with one click into the other brand's own storage and onto its own agent; a sign-in is made once, on
// the page of the brand that lacks it. Once a tool is on a brand the row says so, and only that brand's own card
// removes it: nothing here reaches the other brand. On a host's page its own list names every tool it can share,
// linked or not, so they stay in one place; one it has not linked reads Not connected, with Connect to link it here.
// On a brand profile's page its own list is only what it linked, under its host's tools. A tool both brands hold
// shows once, in the list the page leads with.

function SharingRow({ row, activeName, canManage, canConnect, busy, onCopy, onSignIn, onConnect }) {
  const action = sharingAction(row, { activeName, canManage, canConnect });
  const meta = cardMeta(row);
  const holder = row.direction === "from" ? row.partnerName : activeName;
  const missing = row.status === "not_connected";
  const press = () => (action.kind === "copy" ? onCopy(row) : action.kind === "connect" ? onConnect(row) : onSignIn(row));
  return (
    <li className="cx-share__row" data-testid={`connection-share-${row.direction}-${row.app}`} aria-busy={busy || undefined}>
      <ConnectionLogo app={row.app} label={row.label} size={34} />
      <div className="cx-share__copy">
        <span className="cx-share__name">{row.label}</span>
        <span className="cx-share__sub">{missing ? `Link it on ${activeName} to share it with ${row.partnerName}` : row.accountLabel || `${holder}'s ${row.way === "copy" ? "key" : "sign-in"}`}</span>
      </div>
      <div className="cx-share__end">
        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        {action?.kind === "shared" ? <StatusPill tone="good" testId={`connection-share-${row.direction}-${row.app}-state`}>{action.label}</StatusPill> : null}
        {action?.kind === "blocked" ? <StatusPill tone="warn" testId={`connection-share-${row.direction}-${row.app}-state`}>{action.label}</StatusPill> : null}
        {action?.kind === "copy" || action?.kind === "sign_in" || action?.kind === "connect" ? (
          <button type="button" className={`cx-btn cx-btn--sm${action.kind === "connect" ? "" : " cx-btn--primary"}`} data-testid={`connection-share-${row.direction}-${row.app}-action`} disabled={busy} onClick={press} aria-label={action.kind === "connect" ? `Connect ${row.label} on ${activeName}` : undefined}>
            {busy ? <Loader2 size={13} className="cx-spin" aria-hidden="true" /> : action.kind === "copy" ? <Copy size={13} aria-hidden="true" /> : action.kind === "connect" ? <Plus size={13} aria-hidden="true" /> : <LogIn size={13} aria-hidden="true" />}
            {action.label}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function SharingList({ title, rows, ...rest }) {
  return (
    <div className="cx-share__block">
      <h3 className="cx-share__title">{title}</h3>
      <ul className="cx-share__list">
        {rows.map((row) => <SharingRow key={row.key} row={row} busy={rest.busyKey === `share:${row.key}`} {...rest} />)}
      </ul>
    </div>
  );
}

// `canConnect`: the person manages this brand, so a tool it has not linked can be linked here (`onConnect`).
export function SharingPanel({ sharing, activeName = "this brand", busyKey = null, canConnect = false, onCopy, onSignIn, onConnect }) {
  const partners = (sharing?.partners || []).filter((partner) => (partner.theirs?.length || 0) + (partner.mine?.length || 0) > 0);
  if (!partners.length) return null;
  return partners.map((partner) => {
    const { theirs, mine } = sharingLists(partner);
    const headingId = `connections-sharing-${partner.clientSlug}`;
    const list = { activeName, canManage: Boolean(partner.canManage), canConnect: Boolean(canConnect), busyKey, onCopy, onSignIn, onConnect };
    // The page of the brand that is hosted reads as taking from its host; the host's page reads as giving.
    const hosted = partner.relation === "host";
    return (
      <section key={partner.clientSlug} className="cx-group cx-share" data-testid="connections-sharing" aria-labelledby={headingId}>
        <h2 id={headingId} className="cx-group__head">{hosted ? <>Tools from <b>{partner.clientName}</b></> : <>Share tools with <b>{partner.clientName}</b></>}</h2>
        <p className="cx-share__hint">Each brand keeps its own copy or its own sign-in. Sharing never changes the other brand; to stop sharing, remove the tool from the brand that holds it.</p>
        {hosted ? (
          <>
            {theirs.length ? <SharingList title={`${partner.clientName}'s tools`} rows={theirs} {...list} /> : null}
            {mine.length ? <SharingList title={`${activeName} can share with ${partner.clientName}`} rows={mine} {...list} /> : null}
          </>
        ) : (
          <>
            {mine.length ? <SharingList title={`${activeName}'s tools`} rows={mine} {...list} /> : null}
            {theirs.length ? <SharingList title={`From ${partner.clientName}`} rows={theirs} {...list} /> : null}
          </>
        )}
      </section>
    );
  });
}

export default SharingPanel;
